import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, Loader2, User } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { blogDb, isBlogFirebaseConfigured } from '../lib/firebaseBlog';
import { getCachedPost, setCachedPost } from '../lib/blogCache';
import { BLOG_CATEGORY_OPTIONS, type BlogPost as BlogPostType } from '../dashboard/types/blog';
import { BlogPostView } from '../dashboard/blog/BlogPostView';
import { ArticleOutline } from '../dashboard/blog/ArticleOutline';
import { extractHeadingOutline } from '../dashboard/blog/editor/headingOutline';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null | undefined>(undefined); // undefined = carregando, null = não achado
  const [error, setError] = useState<string | null>(null);
  const outline = useMemo(() => (post ? extractHeadingOutline(post.blocos) : []), [post]);

  useEffect(() => {
    if (!slug) return;
    const cached = getCachedPost(slug);
    if (cached) {
      setPost(cached);
      return;
    }
    if (!blogDb) {
      setPost(null);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(blogDb, 'posts', slug));
        if (!snap.exists() || snap.data().status !== 'publicado') {
          setPost(null);
          return;
        }
        const fetched = { id: snap.id, ...snap.data() } as BlogPostType;
        setCachedPost(fetched);
        setPost(fetched);
      } catch (err) {
        console.error('Falha ao carregar artigo', err);
        setError(err instanceof Error ? err.message : 'Falha ao carregar artigo');
        setPost(null);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-background">
      <Header variant="reading" />

      {/* Zona de leitura com identidade própria ("papel" quente, claro/escuro
          via --reading-bg/--reading — ver .reading-surface em index.css),
          fonte serifada (font-reading/Lora) pra lembrar livro. Grid de 3
          colunas (não 2): a do meio tem largura fixa e fica sempre centrada
          na TELA, independente do sumário existir ou não — "Voltar" e o
          sumário só ocupam as sobras dos dois lados. */}
      <div className="reading-surface">
        <div className="container mx-auto px-4 py-14">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_min(680px,100%)_minmax(0,1fr)] xl:gap-x-16">
            <div className="xl:col-start-1 xl:row-start-1 mb-10 xl:mb-0 xl:pt-1">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-sm text-reading/55 hover:text-reading transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Voltar ao blog
              </Link>
            </div>

            <article className="min-w-0 xl:col-start-2 xl:row-start-1">
              {post === undefined ? (
                <div className="flex items-center justify-center py-24 gap-2 text-reading/55">
                  <Loader2 className="w-5 h-5 animate-spin" /> Carregando artigo…
                </div>
              ) : post === null ? (
                <div className="text-center py-24">
                  <p className="text-xl text-reading/55">
                    {!isBlogFirebaseConfigured()
                      ? 'O blog ainda está sendo configurado. Volte em breve.'
                      : error ?? 'Artigo não encontrado.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Título aparece primeiro, corpo do texto logo em seguida
                      (stagger-2 = 0.2s de atraso) — entrada em 2 tempos, não
                      tudo de uma vez. .fade-in-up (não animate-fade-in-up):
                      essa classe já define opacity:0 como estilo base (não só
                      via keyframe), então o atraso do stagger não deixa o
                      corpo do texto visível antes da hora. */}
                  <div className="fade-in-up">
                    {post.categoria && (
                      <p className="font-reading text-xs font-semibold uppercase tracking-widest text-reading/50 mb-3">
                        {BLOG_CATEGORY_OPTIONS.find((c) => c.id === post.categoria)?.nome}
                      </p>
                    )}
                    <h1 className="font-reading text-2xl md:text-4xl font-bold text-reading leading-[1.2] mb-6">
                      {post.titulo}
                    </h1>

                    <div className="flex items-center gap-3">
                      {post.autorFotoUrl ? (
                        <img src={post.autorFotoUrl} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-reading/10 flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-reading/50" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-wide text-reading/80 truncate">
                          {post.autorNome}
                        </p>
                        {post.publicadoEm && (
                          <p className="text-xs text-reading/50 uppercase tracking-wide mt-0.5">
                            {new Date(post.publicadoEm)
                              .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
                              .replace('.', '')}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="h-px bg-reading/15 mt-6 mb-10" />
                  </div>

                  <BlogPostView
                    blocos={post.blocos}
                    className="prose-reading prose-lg font-reading text-reading fade-in-up stagger-2"
                  />
                </>
              )}
            </article>

            {post && (
              <aside className="hidden xl:block xl:col-start-3 xl:row-start-1">
                <ArticleOutline items={outline} />
              </aside>
            )}
          </div>
        </div>
      </div>

      <Footer variant="reading" />
    </div>
  );
}
